import { Component, computed, inject, signal } from "@angular/core";
import { MatAnchor, MatButton } from "@angular/material/button";
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from "@angular/material/dialog";
import { Router, RouterLink } from "@angular/router";
import { BookingsStore } from "@fry/booking/lib/bookings";
import { OidcService } from "@fry/lib/core";
import { EasTableRow } from "@fry/lib/store";
import { map } from "rxjs";
import { EligibilityRule } from "../eligibility-view/eligibility-view.compoent";
import { APP_BASE_HREF } from "@angular/common";
import { LoadingComponent } from "@fry/ui-material/loading";
import { AlertComponent } from "@fry/ui-material/alert";
import { UIBadgeModule } from "@fry/ui/badge";
import { FryCommonModule } from "@fry/components/common";

interface Eligibility {
    description: string;
    overall: boolean;
    rules: EligibilityRule;
    available_capacity: number;
}

// function isEligibility(data: any): data is Eligibility {
//     if (data === null || typeof data !== 'object') {
//         return false;
//     }
//     return data.eligible !== undefined && data.description !== undefined;
// }

interface BookingApplyModalData {
    row: EasTableRow;
    context: {
        user: string;
    };
}

interface UserInfo {
    user: string;
    currentUser: string;
    isAnonymous: boolean;
    isRemoteUser: boolean;
}

interface ApplyInfo {
    can_apply: boolean;
    eligibility: Eligibility;
    available_capacity: number;
    details: {
        [key: string]: any;
    }

}


@Component({
    selector: 'eas-booking-available-apply',
    templateUrl: 'booking-available-apply.component.html',
    imports: [
        FryCommonModule,
        MatDialogModule,
        MatButton,
        MatAnchor,
        RouterLink,
        LoadingComponent,
        AlertComponent,
        UIBadgeModule,

    ],
    providers: [
    ]
})
export class BookingApplyModalComponent {
    private oidc = inject(OidcService);
    private bookingsStore = inject(BookingsStore)
    private router = inject(Router);
    private baseHref = inject(APP_BASE_HREF);
    dialogRef = inject(MatDialogRef<BookingApplyModalComponent>);
    data: BookingApplyModalData = inject(MAT_DIALOG_DATA);

    $row = computed(() => this.data.row);
    $context = computed(() => this.data.context);

    $applyInfo = signal<ApplyInfo | null>(null);

    $canApply = computed(() => this.$applyInfo()?.can_apply ?? false);
    $eligibility = computed(() => this.$applyInfo()?.eligibility)
    $availableCapacity = computed(() => this.$applyInfo()?.available_capacity ?? -1);
    $bookingOptionData = computed(() => {
        const applyInfo = this.$applyInfo();
        const details = applyInfo?.details || {};
        details['available_capacity'] = applyInfo?.available_capacity || -1;
        return details;
    });

    $isEligible = computed(() => this.$eligibility()?.overall || false);
    $eligibilityTree = computed(() => this.$eligibility()?.rules);
    $eligiblityDescription = computed(() => this.$eligibility()?.description || '');
    $capacityAvailable = computed(() => {
        const capacity = this.$availableCapacity();
        const value = capacity === -1 || capacity > 0;
        return value;
    });
    $bookingOptionTitle = computed(() => this.$row().extra?.title || '');
    $error = signal<string | null>(null);
    $loaded = signal<boolean>(false);

    $userInfo = computed<UserInfo>(() => {
        const currentUser = this.oidc.currentUser().username || 'anonymous';
        const user = this.$context()?.user || currentUser;
        return {
            user: user,
            currentUser: currentUser,
            isAnonymous: currentUser === 'anonymous',
            isRemoteUser: user !== currentUser
        }
    })

    $showApply = computed(() => this.$isEligible() && this.$capacityAvailable() && this.$canApply() && !this.$operationInProgress());

    $operationInProgress = signal<boolean>(false);

    private bookingItem: string = '';
    private bookingOption: string = '';

    constructor() {

        // if (isEligibility(this.data.extra?.eligibility)) {
        //     this.$eligibility.set(this.data.extra.eligibility);
        // }

        const splitted = this.$row().id.split(':');
        if (splitted.length !== 2) {
            throw new Error('Invalid booking id');
        }
        this.bookingItem = splitted[0];
        this.bookingOption = splitted[1];
    }

    ngOnInit() {
        this.setup();
    }

    setup() {
        this.$operationInProgress.set(true);
        if (!this.$userInfo().isAnonymous) {
            const user = this.$userInfo().user;
            this.bookingsStore.applyInfo(user, this.bookingItem, this.bookingOption, {}).pipe(
                map(res => {
                    this.$applyInfo.set(res);
                    this.$loaded.set(true);
                    if (!this.$capacityAvailable()) {
                        this.$error.set($localize `We're sorry there is no longer any availability for this booking`);
                    }
                    this.$operationInProgress.set(false);
                })
            ).subscribe({
                error: err => {
                    console.log(err);
                    this.$error.set(err.toString());
                    this.$operationInProgress.set(false);
                    this.$loaded.set(true);
                }
            });
        } else {
            this.$applyInfo.set({
                can_apply: this.$row().extra?.canApply || false,
                eligibility: {
                    description: this.$row().extra?.eligibility?.description || '',
                    overall: false,
                    rules: this.$row().extra?.eligibility?.rules || null,
                    available_capacity: -1
                },
                available_capacity: -1,
                details: {}
            });

            this.$loaded.set(true);
            this.$operationInProgress.set(false);}
    }

    onApply() {
        this.$error.set(null);
        if (!this.$showApply()) {
            return;
        };
        this.$operationInProgress.set(true);
        this.bookingsStore.apply(this.$context().user, this.bookingItem, this.bookingOption, {}).subscribe({
            next: res => {
                this.router.navigate(['/bookings', res.result.id, 'edit']);
                this.dialogRef.close();
                this.$operationInProgress.set(false);
            },
            error: err => {
                console.log(err);
                this.$error.set(err.toString());
                this.$operationInProgress.set(false);
            }
        });
    }

    onLogin() {
        this.oidc.setOrganisation(this.oidc.organisation);
        const url = this.$userInfo().isRemoteUser
            ? this.router.createUrlTree([this.baseHref, 'users', this.$userInfo().user, 'apply', this.bookingItem, this.bookingOption])
            : this.router.createUrlTree([this.baseHref, 'bookings', 'apply', this.bookingItem, this.bookingOption]);

        window.location.href = url.toString();
    }

    onCancel() {
        this.dialogRef.close();
    }
}
